import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Classable, Entity, HasChildren, Shapeable, Slice } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { classNames, entityTypeNameFor } from '@shapeable/utils';
import { LinkButton, useActivePersonLive, usePageEntity } from '@shapeable/ui';
import { canEditEntityPage, getHubEditHref, isOwnPersonPage } from '../../utils/can-edit-entity-page';
import { PRIMARY } from '../../theme';
import { EditIconGlyph } from '@shapeable/icons';

const cls = classNames('slice-edit-entity-button');

export type SliceEditEntityButtonProps = Classable & HasChildren & {
  entity?: Slice;
};

export const SliceEditEntityButtonDefaultProps: Omit<SliceEditEntityButtonProps, 'entity'> = {};

type ContainerProps = {};

const fadeIn = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const ContainerStyles = breakpoints({
  base: css`
    padding: ${theme.UNIT(3)} ${theme.UNIT(3)} ${theme.UNIT(5)};
    border-bottom: 1px solid ${theme.COLOR('canvas')};
    animation: ${fadeIn} 0.3s ease forwards;
  `,
  tablet: css`
    padding: ${theme.UNIT(3)} ${theme.UNIT(3)} ${theme.UNIT(5)};
  `,
  desktopDevice: css`
    padding: ${theme.UNIT(3)} ${theme.UNIT(5)} ${theme.UNIT(5)};
    ${theme.APPLY_COLORS};
  `,
});

const ButtonStyles = breakpoints({
  base: css`
    max-width: fit-content;

    .shp--button {
      background: ${PRIMARY};
      border: none;
      border-radius: ${theme.UNIT(8)};

      color: ${theme.COLOR('light')};
      border-radius: ${theme.UNIT(8)};
      font-size: 0.8em;
      font-weight: 600;
      transition: all 0.3s ease;
      
      &:hover {
        filter: brightness(1.1);
      }
      
    }
  `,
});

const My = {
  Container: styled.div<ContainerProps>`${ContainerStyles}`,
    Button: styled(LinkButton)`${ButtonStyles}`,
};

export const SliceEditEntityButton: Shapeable.FC<SliceEditEntityButtonProps> = (props) => {
  const { className, children } = props;

  const activePage = usePageEntity() as Entity;
  const { person, isAvailable, isLoading } = useActivePersonLive();

  const entityTypeName = entityTypeNameFor(activePage);
  const typeLabel = activePage?.typeLabel || activePage?._schema?.label || entityTypeName;
  const canEdit = canEditEntityPage(activePage, person, isAvailable);
  const href = getHubEditHref(activePage, person);

  if (isLoading || !canEdit || !href) {
    return null;
  }

  const label = children || (isOwnPersonPage(activePage, person) ? 'EDIT YOUR PROFILE' : `Edit this ${typeLabel}`);

  return (
    <My.Container className={cls.name(className)} >
      <My.Button
        href={href}
        linkProps={{ target: '_self' }}
        icon={EditIconGlyph}
        iconSize={15}
        colors={{ fill: 'light' }}
        variant='slim'
        className={cls.name(className)}
      >
        {label}
      </My.Button>
    </My.Container>
  );
};

SliceEditEntityButton.defaultProps = SliceEditEntityButtonDefaultProps;
SliceEditEntityButton.cls = cls;
